import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import isUndefined from 'lodash/isUndefined';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useAnalytics } from '@modernhealth/web-analytics';

import { navigationService } from 'src/config/rootService';
import { SOURCE_TYPES } from 'src/modules/user/models/user';
import { CrisisModal } from 'src/shared/components/CrisisModal';
import { Button } from 'src/shared/components/Button';
import { DeprecatedButton as NewButton } from '@modernhealth/vital-components';
import { color, font, layout, mediaQueries } from 'src/shared/styles/variables';

import {
    AssessmentTestId,
    FieldType,
    MappedAnswers,
    MultiSelectQuestion as IMultiSelectQuestion,
    Question,
    SingleSelectQuestion as ISingleSelectQuestion,
    AssessmentType,
} from '../models/assessments';
import { getHeaderTextByAssessmentName } from '../selectors/assessmentsSelectors';
import { AssessmentProgressBar } from './AssessmentProgressBar';
import { AssessmentQuestion } from './AssessmentQuestion';
import { RootState } from 'src/config/rootStore';

const Wrapper = styled.div({
    display: 'flex',
    width: '100%',
    height: '100%',
    flexGrow: 1,
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 100,
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const TopControls = styled.div({
    position: 'absolute',
    top: 150,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    maxWidth: layout.containerSmallerWidth,
    [mediaQueries.sm]: {
        flexDirection: 'column',
        paddingBottom: '2rem',
        marginBottom: '2rem',
        borderBottom: `1px solid ${color.gallery}`,
    },
});

const QuestionWrapper = styled.div({
    position: 'relative',
    maxWidth: layout.containerBiggerWidth,
    textAlign: 'center',
    [mediaQueries.sm]: {
        width: '100%',
        paddingBottom: 0,
    },
});

const AnimationContainer = styled.div<{ direction: 'left' | 'right'; isMultiSelect: boolean }>(
    ({ direction, isMultiSelect }) => ({
        maxWidth: isMultiSelect ? 1100 : 700,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',

        '&.assessment-enter': {
            opacity: 0,
            transform: `translate(${direction === 'left' ? '50%' : '-50%'}, 0)`,
        },
        '&.assessment-enter-active': {
            opacity: 1,
            transition: 'transform 250ms ease-in, opacity 350ms ease-in',
            transform: 'translate(0,0)',
        },
        '&.assessment-exit': {
            transform: 'translate(0,0)',
        },
        '&.assessment-exit-active': {
            opacity: 0,
            transform: `translate(${direction === 'left' ? '-25%' : '25%'}, 0)`,
            transition: 'opacity 200ms ease-out, transform 250ms',
        },
    }),
);

const Title = styled.h2(
    {
        color: color.chathamsBlue,
        [mediaQueries.sm]: {
            marginBottom: 0,
        },
    },
    font.heavy(font.size.medium),
);

const DashboardLink = styled.a(
    {
        color: color.mamba,
        '&:hover': {
            color: color.chathamsBlue,
        },
        [mediaQueries.sm]: {
            marginTop: '0.5rem',
            textDecoration: 'underline',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
    },
    font.medium(font.size.medium),
);

const fadeIn = keyframes`{
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
     }
}`;

const NavigationButton = styled(Button)<{ disabled?: boolean }>(() => ({
    '&&': {
        minWidth: 241,
        maxWidth: 241,

        animation: `${fadeIn} 400ms ease`,
        transition: 'background-color 200ms ease',

        '&.disabled': {
            backgroundColor: color.casper,
            borderColor: color.casper,
            cursor: 'not-allowed',
            color: color.white,
        },

        '&.disabled:hover': {
            backgroundColor: color.gullGray,
            borderColor: color.gullGray,
        },
    },
}));

const NavigationContainer = styled.div({
    marginTop: 40,
    display: 'flex',
});

const CrisisLink = styled.div({
    display: 'flex',
    justifyContent: 'center',
});

const getCurrentAssessmentName = (questionKey: string) => {
    const arr = questionKey.split('_');
    arr.pop();

    return arr.join('_');
};

const getCompletedAnswersPercent = (answers: MappedAnswers, questionsAmount: number) => {
    const completedAnswersAmount = Object.entries(answers).filter(
        ([_k, val]) => val === null || val.toString() !== '',
    ).length;

    return Math.floor((completedAnswersAmount / questionsAmount) * 100);
};

export const areAllRequiredQuestionsAnswered = (answers: MappedAnswers, questions: Question[]) => {
    const requiredQuestions = questions.filter(question => !question.optional);

    const answersValidity = requiredQuestions.map(question => {
        const answer = answers[question.questionKey];

        return answer !== '' && !isUndefined(answer);
    });

    return answersValidity.every(valid => valid === true);
};

interface Props {
    questions: Question[] | IMultiSelectQuestion[];
    onSubmit?: (answers: MappedAnswers) => void;
    onBack?: () => void;
    showTopControls?: boolean;
    hideProgressBar?: boolean;
    customHeader?: (question: IMultiSelectQuestion | ISingleSelectQuestion) => React.ReactNode;
    assessmentName?: string; // This should change to required once we enable modularized assessments
    assessmentFlowUuid?: string;
}

const valueForSkippedQuestion = null;

export const Assessment: React.FC<Props> = ({
    showTopControls,
    questions,
    onSubmit,
    hideProgressBar,
    customHeader,
    assessmentName,
    assessmentFlowUuid = 'No assessment flow uuid provided',
    onBack,
}) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({} as MappedAnswers);
    const [submitInProgress, setSubmitInProgress] = useState(false);
    const [crisisModalVisible, setCrisisModalVisible] = useState(false);
    const [animationDirection, setAnimationDirection] = useState<'left' | 'right'>('left');
    const isMounted = useRef(false);

    const { isTrackingWho5On, isTrackingGadOn, isTrackingPhqOn, isTrackingEmploymentHealthOn } =
        useFlags();

    const currentQuestion = questions[step];
    const isQuestionOptional = currentQuestion?.optional === true;

    const currentAssessmentId = currentQuestion?.questionKey;
    const currentAssessmentName = assessmentName || getCurrentAssessmentName(currentAssessmentId);

    const shouldTrackWho5 = isTrackingWho5On && !isQuestionOptional;

    const assessmentTrackingFlags = {
        [AssessmentType.Who5]: shouldTrackWho5,
        [AssessmentType.Phq2]: isTrackingPhqOn,
        [AssessmentType.Phq8]: isTrackingPhqOn,
        [AssessmentType.Phq8Remainder]: isTrackingPhqOn,
        [AssessmentType.Phq9]: isTrackingPhqOn,
        [AssessmentType.Phq9Remainder]: isTrackingPhqOn,
        [AssessmentType.Gad2]: isTrackingGadOn,
        [AssessmentType.Gad7]: isTrackingGadOn,
        [AssessmentType.Gad7Remainder]: isTrackingGadOn,
        [AssessmentType.EmploymentHealth]: isTrackingEmploymentHealthOn,
    };
    const shouldTrackCurrentAssessment = assessmentTrackingFlags[currentAssessmentName];

    const headerText = useSelector((state: RootState) =>
        getHeaderTextByAssessmentName(state, currentAssessmentName),
    );

    const currentAssessmentGroup = currentAssessmentId.startsWith(AssessmentType.Who5)
        ? currentAssessmentId
        : currentAssessmentId.replace(/\d+(remainder)?/g, ''); // Converts 'phq2', 'phq9', or 'phq9remainder into just 'phq', leaves 'who5' as is

    const onQuestionMount = useCallback(() => {
        if (shouldTrackCurrentAssessment) {
            const eventName = `Assessment ${currentAssessmentGroup}`;
            analytics.trackSinglePageFlow(eventName, {
                flow: 'Assessment',
                assessment_flow_id: assessmentFlowUuid,
                assessment_id: currentAssessmentName,
            });
        }
    }, [
        shouldTrackCurrentAssessment,
        currentAssessmentName,
        currentAssessmentGroup,
        assessmentFlowUuid,
    ]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    if (!questions.length) {
        return null;
    }

    const goToNextQuestion = () => {
        if (!isCurrentQuestionAnswered) {
            return;
        }
        setAnimationDirection('left');
        setTimeout(() => {
            setStep(step + 1);
        }, 100);
    };

    const handleGoBack = () => {
        if (firstStep && onBack) {
            onBack();
            return;
        }

        setAnimationDirection('right');
        setTimeout(() => {
            setStep(step - 1);
        }, 100);
    };

    const handleSubmit = async (optionalAnswers?: MappedAnswers) => {
        if (onSubmit) {
            setSubmitInProgress(true);

            await onSubmit(optionalAnswers ? optionalAnswers : answers);

            if (!isMounted.current) {
                return;
            }
            setSubmitInProgress(false);
        }
    };

    const isCurrentQuestionAnswered = (): boolean => {
        const answer = answers[questions[step].questionKey];

        if (questions[step].optional && answer === valueForSkippedQuestion) {
            return true;
        }

        return answer !== undefined && answer?.toString() !== '';
    };

    const questionsAmount = questions.length;
    const isSurveyValid = areAllRequiredQuestionsAnswered(answers, questions);
    const hasAnswer = isCurrentQuestionAnswered();
    const answeredPercent = getCompletedAnswersPercent(answers, questionsAmount);
    const firstStep = step === 0;
    const lastStep = step === questionsAmount - 1;
    const shouldDisplayBackButton = !firstStep || onBack;

    const handleQuestionAnswerChange = (
        questionId: string,
        value: number | null | string[] | string,
    ) => {
        const newAnswers = {
            ...answers,
            [questionId]: value,
        };

        setAnswers(newAnswers);
        return newAnswers;
    };

    const isContinueButtonDisabled = () => {
        if (lastStep) {
            return !isSurveyValid || !hasAnswer;
        }

        return !hasAnswer;
    };

    const handleQuestionSkip = (questionId: string) => {
        const value = currentQuestion.type === FieldType.MultiSelect ? [] : valueForSkippedQuestion;
        handleQuestionAnswerChange(questionId, value);
        if (lastStep) {
            handleSubmit();
        } else {
            goToNextQuestion();
        }
    };

    const handleGoToNextQuestion = () => {
        if (isContinueButtonDisabled()) {
            return;
        }

        if (lastStep) {
            handleSubmit();
        } else {
            goToNextQuestion();
        }
    };

    return (
        <Wrapper>
            <TopControls>
                {showTopControls ? (
                    <>
                        <Title>{t('who5Survey.title')}</Title>
                        <DashboardLink onClick={() => navigationService.navigate('/dashboard')}>
                            {t('who5Survey.quit')}
                        </DashboardLink>
                    </>
                ) : null}
            </TopControls>

            <QuestionWrapper data-testid={`${AssessmentTestId.QuestionContainer}_${step}`}>
                <SwitchTransition>
                    <CSSTransition key={step} timeout={400} classNames="assessment">
                        <AnimationContainer
                            direction={animationDirection}
                            isMultiSelect={currentQuestion.type === FieldType.MultiSelect}
                        >
                            <AssessmentQuestion
                                customHeader={customHeader}
                                onMount={onQuestionMount}
                                question={currentQuestion as IMultiSelectQuestion}
                                headerText={headerText}
                                answer={answers[currentQuestion.questionKey]}
                                onAnswerChange={handleQuestionAnswerChange}
                                onAnswerSubmit={handleGoToNextQuestion}
                            >
                                {hideProgressBar || (lastStep && isSurveyValid) ? null : (
                                    <AssessmentProgressBar percent={answeredPercent} />
                                )}
                            </AssessmentQuestion>
                            {currentQuestion.displayCrisisWarning && (
                                <CrisisLink>
                                    <NewButton
                                        title={t('phq9.need_crisis_support')}
                                        onClick={() => setCrisisModalVisible(true)}
                                        variant="flat"
                                        size="large"
                                        testID={AssessmentTestId.ImmediateCrisisSupportButton}
                                    />
                                </CrisisLink>
                            )}

                            <CrisisModal
                                visible={crisisModalVisible}
                                source={SOURCE_TYPES.PHQ_9_LINK}
                                onClose={() => {
                                    setCrisisModalVisible(false);
                                }}
                            />
                        </AnimationContainer>
                    </CSSTransition>
                </SwitchTransition>
            </QuestionWrapper>
            <NavigationContainer>
                {shouldDisplayBackButton && (
                    <NavigationButton
                        type="white"
                        onClick={handleGoBack}
                        testID={AssessmentTestId.PreviousQuestion}
                    >
                        {t('common.back')}
                    </NavigationButton>
                )}
                <NavigationButton
                    type="accept"
                    onClick={handleGoToNextQuestion}
                    marginLeft={shouldDisplayBackButton ? 16 : 0}
                    testID={AssessmentTestId.NextQuestion}
                    loading={submitInProgress}
                    className={isContinueButtonDisabled() ? 'disabled' : ''}
                >
                    {t(
                        currentQuestion.type !== FieldType.MultiSelect && lastStep
                            ? 'common.submit'
                            : 'common.continue',
                    )}
                </NavigationButton>
            </NavigationContainer>
            {currentQuestion.optional || currentQuestion.type === FieldType.MultiSelect ? (
                <NavigationButton
                    onClick={() => handleQuestionSkip(currentQuestion.questionKey)}
                    marginTop={5}
                    type="noBackground"
                    noHorizontalPadding
                >
                    {t(
                        currentQuestion.type === FieldType.MultiSelect
                            ? 'common.none_of_these'
                            : 'who5Survey.skipQuestion',
                    )}
                </NavigationButton>
            ) : null}
        </Wrapper>
    );
};
